import React, { useEffect, useState, useCallback } from 'react';
import { Table, Tag, Button, Space, Statistic, Row, Col, Card, Select, Input, Empty, message } from 'antd';
import { PrinterOutlined, UserOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { dentalService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import { printDentalPlan } from '../Print/printDentalPlan';
import { INVOICE_STATUSES, formatMoney, invoiceStatus, translate } from '../../utils/dental';

// Every treatment plan as an invoice: its number, what it came to, what has
// been paid and what is still owed. Read-only - an invoice is changed by
// changing the plan or taking a payment, both of which happen on the patient's
// page.
const InvoicesTab = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ status: undefined, from: '', to: '', q: '' });
    // What is typed in the search box; it becomes a filter when the typing stops.
    const [search, setSearch] = useState('');

    const tr = (key, fallback) => translate(t, key, fallback);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await dentalService.getInvoices(filters);
            setInvoices(response.data || []);
            setSummary(response.summary || {});
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    }, [filters, t]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters((current) => (current.q === search ? current : { ...current, q: search }));
        }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    // The treating dentist's letterhead: the dentist's own, or - for a
    // secretary - the one cached at login.
    const letterhead = () => {
        if (user?.role === 'doctor') return user;
        try {
            return JSON.parse(localStorage.getItem('doctorProfile') || 'null') || {};
        } catch (error) {
            return {};
        }
    };

    const print = (invoice) => {
        const opened = printDentalPlan({
            plan: invoice,
            patient: invoice.patientId,
            doctorInfo: letterhead(),
            lang: i18n.language,
            t,
            kind: 'invoice'
        });
        if (!opened) message.warning(t('popupBlocked'));
    };

    const openPatient = (invoice) => {
        if (invoice.patientId?._id) navigate(`/${user.role}/patient/${invoice.patientId._id}?tab=dental`);
    };

    return (
        <>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                {[
                    ['invoiced', tr('invoiced', 'Invoiced'), undefined],
                    ['paid', tr('paid', 'Paid'), '#3f8600'],
                    ['outstanding', tr('outstanding', 'Outstanding'), summary.outstanding > 0 ? '#cf1322' : undefined]
                ].map(([key, title, color]) => (
                    <Col xs={24} sm={8} key={key}>
                        <Card size="small">
                            <Statistic title={title} value={formatMoney(summary[key])} valueStyle={{ fontSize: 20, color }} />
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
                <Col xs={24} md={8}>
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder={tr('invoiceSearch', 'Number, patient or plan')}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </Col>
                <Col xs={24} sm={8} md={6}>
                    <Select
                        allowClear
                        style={{ width: '100%' }}
                        placeholder={t('status')}
                        value={filters.status}
                        onChange={(status) => setFilters((current) => ({ ...current, status }))}
                        options={INVOICE_STATUSES.map(([value, key, fallback]) => ({ value, label: tr(key, fallback) }))}
                    />
                </Col>
                <Col xs={12} sm={8} md={5}>
                    <Input
                        type="date"
                        aria-label={tr('fromDate', 'From')}
                        value={filters.from}
                        onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))}
                    />
                </Col>
                <Col xs={12} sm={8} md={5}>
                    <Input
                        type="date"
                        aria-label={tr('toDate', 'To')}
                        value={filters.to}
                        onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))}
                    />
                </Col>
            </Row>

            <Table
                rowKey="_id"
                loading={loading}
                dataSource={invoices}
                scroll={{ x: 860 }}
                pagination={{ pageSize: 20 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={tr('noInvoices', 'No invoices')} /> }}
                columns={[
                    {
                        title: tr('invoiceNumber', 'Invoice no.'),
                        dataIndex: 'invoiceNumber',
                        width: 150,
                        render: (value) => <span dir="ltr">{value || '-'}</span>
                    },
                    {
                        title: tr('date', 'Date'),
                        dataIndex: 'createdAt',
                        width: 110,
                        render: (value) => dayjs(value).format('YYYY-MM-DD')
                    },
                    {
                        title: t('patients'),
                        render: (_, invoice) => (
                            <Space direction="vertical" size={0}>
                                <span>{invoice.patientId?.name || '-'}</span>
                                {invoice.patientId?.phoneNumber && <small style={{ color: '#888' }}>{invoice.patientId.phoneNumber}</small>}
                            </Space>
                        )
                    },
                    { title: tr('dentalPlan', 'Treatment plan'), dataIndex: 'title' },
                    { title: tr('total', 'Total'), align: 'end', render: (_, invoice) => formatMoney(invoice.totals.total, invoice.currency) },
                    { title: tr('paid', 'Paid'), align: 'end', render: (_, invoice) => formatMoney(invoice.totals.paid, invoice.currency) },
                    {
                        title: tr('remaining', 'Remaining'),
                        align: 'end',
                        render: (_, invoice) => <strong>{formatMoney(invoice.invoiceStatus === 'cancelled' ? 0 : invoice.totals.remaining, invoice.currency)}</strong>
                    },
                    {
                        title: t('status'),
                        dataIndex: 'invoiceStatus',
                        width: 110,
                        render: (status) => <Tag color={invoiceStatus.color(status)}>{invoiceStatus.label(t, status)}</Tag>
                    },
                    {
                        width: 96,
                        render: (_, invoice) => (
                            <Space size={4}>
                                <Button
                                    icon={<PrinterOutlined />}
                                    title={t('print')}
                                    aria-label={t('print')}
                                    onClick={() => print(invoice)}
                                />
                                <Button
                                    icon={<UserOutlined />}
                                    title={t('patients')}
                                    aria-label={t('patients')}
                                    disabled={!invoice.patientId?._id}
                                    onClick={() => openPatient(invoice)}
                                />
                            </Space>
                        )
                    }
                ]}
            />
        </>
    );
};

export default InvoicesTab;
